import AddIcon from "@mui/icons-material/Add";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Divider, IconButton, InputAdornment, type SxProps } from "@mui/material";
import { type FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { DaysInWeek, DaysInWeekSelector } from "../../../../components/RecurringShift/util/DaysInWeekSelector";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { RoleChips } from "../../../../components/UIToolkit/molecules/RoleChips";
import { useJobColors } from "../../../../hooks/useJobColors";
import { useJobs } from "../../../../hooks/useJobs";
import { STATUS } from "../../../../theme/tokens";
import { NEUTRAL_CATEGORICAL_COLOR } from "../../../../utils/categoricalColors";
import { censusBoundLabel, RATIO_LADDER_UNBOUNDED_CENSUS_END } from "../../../../utils/censusResolution";
import type { NurseRole, RatioLadder } from "../../../../utils/models";
import { addLadderRow, canAddLadderRow, getConflictingDaysByRow, hasValidAmount, hasValidRange, rowDays, setLadderRowMax } from "./ratioLadderCoverage";

const compactNumericFieldSx: SxProps = { width: "75px" } as const;
/** Wider than its siblings: it also holds the "no upper limit" button. */
const maxFieldSx: SxProps = { width: "108px" } as const;

const dashedDividerSx: SxProps = { borderStyle: "dashed" } as const;

/**
 * What the admin typed into Max. Only the ∞ button sets "no upper limit"; typing replaces the "∞" shown
 * in the field, so anything but digits is dropped. Blank stays blank (NaN) and blocks save.
 */
const parseTypedMax = (typed: string): number => {
	const digits = typed.replace(/\D/g, "");
	return digits === "" ? Number.NaN : Number.parseInt(digits, 10);
};

interface RatioLadderSectionProps {
	ratioLadder: RatioLadder[];
	onRatioLadderChange: (ratioLadder: RatioLadder[]) => void;
	locationRoles: NurseRole[];
	locationJobId: string;
	jobManagementEnabled?: boolean;
}

export const RatioLadderSection: FC<RatioLadderSectionProps> = ({ ratioLadder, onRatioLadderChange, locationRoles, locationJobId, jobManagementEnabled }) => {
	const { t } = useTranslation();
	const enabledJobs = useJobs();
	const jobColor = useJobColors();
	const showRoleChips = jobManagementEnabled ? false : locationRoles.length > 1;
	const showJobChips = jobManagementEnabled && enabledJobs.length > 1;
	const conflictingDaysByRow = getConflictingDaysByRow(ratioLadder);
	const [focusedNewRowId, setFocusedNewRowId] = useState<string | null>(null);

	const handleAddRatio = () => {
		const { ladder, newRowId } = addLadderRow(ratioLadder, { roles: locationRoles, jobId: locationJobId });
		setFocusedNewRowId(newRowId);
		onRatioLadderChange(ladder);
	};

	// The ladder as it was when a Max field took focus. Every keystroke in that field is applied to it, so
	// the rows that follow keep their place while the admin clears and retypes (see `setLadderRowMax`).
	const maxEditStart = useRef<{ rowId: string; ladder: RatioLadder[] } | null>(null);
	const handleSetMax = (row: RatioLadder, max: number) => {
		const start = maxEditStart.current;
		onRatioLadderChange(setLadderRowMax(start?.rowId === row.id ? start.ladder : ratioLadder, row.id, max));
	};

	const handleUpdateRatio = (id: string, updates: Partial<RatioLadder>) => {
		const newRatios = ratioLadder.map((ratio) => (ratio.id === id ? { ...ratio, ...updates } : ratio));
		onRatioLadderChange(newRatios);
	};

	const handleDeleteRatio = (id: string) => {
		const newRatios = ratioLadder.filter((ratio) => ratio.id !== id);
		onRatioLadderChange(newRatios);
	};

	return (
		<Box
			onClick={(e) => e.stopPropagation()}
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				width: "100%",
				alignItems: "start",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					width: "100%",
				}}
			>
				{[...ratioLadder]
					.sort((a, b) => a.censusStart - b.censusStart)
					.map((ratio, index) => {
						const isLastRow = index === ratioLadder.length - 1;
						const hasRolesError = showRoleChips && ratio.roles.length === 0;
						const hasJobsError = showJobChips && !ratio.jobId;
						const hasAmountError = !hasValidAmount(ratio);
						const hasDaysError = rowDays(ratio).length === 0;
						const conflictingDays = conflictingDaysByRow.get(ratio.id);
						const hasCensusConflict = !!conflictingDays;
						const isOpenEnded = ratio.censusEnd === RATIO_LADDER_UNBOUNDED_CENSUS_END;
						const hasRangeError = !hasValidRange(ratio);
						const foundJob = enabledJobs.find((j) => j.id === ratio.jobId);
						const jobDisplayName = foundJob?.displayName;
						return (
							<Box
								key={ratio.id}
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 0.5,
									width: "100%",
								}}
							>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									{showJobChips && (
										<Box
											sx={{
												display: "flex",
												gap: 1,
												alignItems: "center",
												...(hasJobsError ? { outline: `1px solid ${STATUS.ERROR.main}`, borderRadius: "8px", p: "2px" } : {}),
											}}
										>
											<Box
												sx={{
													bgcolor: `${ratio.jobId ? jobColor(ratio.jobId).dot : NEUTRAL_CATEGORICAL_COLOR.dot} !important`,
													borderRadius: "50%",
													width: "10px !important",
													height: "10px !important",
												}}
											/>
											{jobDisplayName}
										</Box>
									)}
									{showRoleChips && (
										<Box sx={hasRolesError ? { outline: `1px solid ${STATUS.ERROR.main}`, borderRadius: "8px", p: "2px" } : undefined}>
											<RoleChips
												hideAllOption
												availableRoles={locationRoles}
												onSelectedRoleFiltersChanged={(selectedRoles) => {
													handleUpdateRatio(ratio.id, {
														roles: selectedRoles.filter((r) => r !== "all"),
													});
												}}
												initiallySelected={ratio.roles}
											/>
										</Box>
									)}
									<SystemValidationTextField
										inputType={"number"}
										label={"Min"}
										value={ratio.censusStart.toString()}
										onChange={(event) => {
											handleUpdateRatio(ratio.id, { censusStart: Number.parseInt(event.target.value, 10) });
										}}
										inputProps={{ inputProps: { min: 0 } }}
										inputSx={compactNumericFieldSx}
										reserveHeightForHelperText={false}
										defaultErrorColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
										defaultBorderColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
									/>
									<SystemValidationTextField
										inputType={"text"}
										label={"Max"}
										value={Number.isNaN(ratio.censusEnd) ? "" : censusBoundLabel(ratio.censusEnd)}
										onChange={(event) => handleSetMax(ratio, parseTypedMax(event.target.value))}
										inputProps={{
											inputProps: { inputMode: "numeric" },
											autoFocus: ratio.id === focusedNewRowId,
											onFocus: () => {
												maxEditStart.current = { rowId: ratio.id, ladder: ratioLadder };
											},
											onBlur: () => {
												maxEditStart.current = null;
											},
											endAdornment: (
												<InputAdornment position="end">
													<InHouseTooltip title={t("schedule.template.ratio.ladder.max.unbounded")}>
														<IconButton
															size="small"
															edge="end"
															aria-label={t("schedule.template.ratio.ladder.max.unbounded")}
															aria-pressed={isOpenEnded}
															color={isOpenEnded ? "primary" : "default"}
															onClick={() => handleSetMax(ratio, RATIO_LADDER_UNBOUNDED_CENSUS_END)}
															data-testid={`ratio-ladder-max-unbounded-button-${ratio.id}`}
														>
															<AllInclusiveIcon fontSize="inherit" />
														</IconButton>
													</InHouseTooltip>
												</InputAdornment>
											),
										}}
										inputSx={maxFieldSx}
										reserveHeightForHelperText={false}
										defaultErrorColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
										defaultBorderColor={hasCensusConflict || hasRangeError ? STATUS.ERROR.main : undefined}
										dataTestId={`ratio-ladder-max-input-${ratio.id}`}
									/>
									<SystemValidationTextField
										inputType={"number"}
										label={"Amount"}
										value={ratio.amount.toString()}
										onChange={(event) => {
											handleUpdateRatio(ratio.id, { amount: Number.parseInt(event.target.value, 10) });
										}}
										inputProps={{ inputProps: { min: 0 } }}
										inputSx={compactNumericFieldSx}
										reserveHeightForHelperText={false}
										defaultErrorColor={hasAmountError ? STATUS.ERROR.main : undefined}
										defaultBorderColor={hasAmountError ? STATUS.ERROR.main : undefined}
									/>
									<IconButton size="small" onClick={() => handleDeleteRatio(ratio.id)}>
										<DeleteIcon fontSize="small" />
									</IconButton>
									{isLastRow && canAddLadderRow(ratioLadder) && (
										<IconButton size="small" onClick={handleAddRatio}>
											<AddIcon fontSize="small" />
										</IconButton>
									)}
								</Box>
								<Box sx={hasDaysError ? { outline: `1px solid ${STATUS.ERROR.main}`, borderRadius: "8px", p: "2px", width: "fit-content" } : undefined}>
									<DaysInWeekSelector
										size="small"
										gap="2px"
										selectedDays={Object.values(DaysInWeek).filter((fullDay) => rowDays(ratio).some((short) => fullDay.toLowerCase().startsWith(short)))}
										onChange={(selectedDays) =>
											handleUpdateRatio(ratio.id, {
												days: selectedDays.map((d) => d.toLowerCase().slice(0, 3)),
											})
										}
										errorDays={
											conflictingDays ? Object.values(DaysInWeek).filter((fullDay) => conflictingDays.has(fullDay.toLowerCase().slice(0, 3))) : undefined
										}
									/>
								</Box>
								{index < ratioLadder.length - 1 && <Divider sx={dashedDividerSx} />}
							</Box>
						);
					})}
				{ratioLadder.length === 0 && (
					<Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
						<IconButton size="small" onClick={handleAddRatio}>
							<AddIcon fontSize="small" />
						</IconButton>
					</Box>
				)}
			</Box>
		</Box>
	);
};
